export interface GenArtModelMetricSpec {
	readonly title: string;
	readonly subtitle: string;
}

export interface GenArtModelUISpec {
	readonly param1: GenArtModelMetricSpec;
	readonly param2: GenArtModelMetricSpec;
	readonly param3: GenArtModelMetricSpec;
}

export interface GenArtModelDescriptor {
	readonly id: string;
	readonly providerKey: string;
	readonly providerDisplayName: string;
	readonly modelKey: string;
	readonly displayName: string;
	readonly callToActionTint: string;
	readonly musicGenerationPrice: number;
	readonly uiSpec: GenArtModelUISpec;
	readonly isDefault: boolean;
	readonly has3DModel: boolean;
	readonly heroArtworkPath: string;
}

export interface DesignerPromptSuggestion {
	readonly id: string;
	readonly caption: string;
	readonly prompt: string;
	readonly artworkGradient: string;
	readonly artworkURL?: string;
}

export const GENART_MODELS: readonly GenArtModelDescriptor[] = [
	{
		id: "minimax-3-0",
		providerKey: "minimax",
		providerDisplayName: "MiniMax",
		modelKey: "minimax_music_3_0",
		displayName: "MiniMax 3.0",
		callToActionTint: "#7373F2",
		musicGenerationPrice: 10,
		uiSpec: {
			param1: { title: "V3.0", subtitle: "模型版本" },
			param2: { title: "10", subtitle: "灵感 / 次" },
			param3: { title: "歌词 + 旋律", subtitle: "完整生成" },
		},
		isDefault: true,
		has3DModel: true,
		heroArtworkPath: "/images/genart/playlist-default-purple.png",
	},
	{
		id: "suno-5-5",
		providerKey: "suno",
		providerDisplayName: "Suno",
		modelKey: "suno_v5_5",
		displayName: "Suno v5.5",
		callToActionTint: "#E0589E",
		musicGenerationPrice: 12,
		uiSpec: {
			param1: { title: "V5.5", subtitle: "模型版本" },
			param2: { title: "12", subtitle: "灵感 / 次" },
			param3: { title: "歌词 + 旋律", subtitle: "完整生成" },
		},
		isDefault: false,
		has3DModel: false,
		heroArtworkPath: "/images/genart/playlist-default-orange.png",
	},
	{
		id: "udio-1-5",
		providerKey: "udio",
		providerDisplayName: "Udio",
		modelKey: "udio_1_5",
		displayName: "Udio 1.5",
		callToActionTint: "#3FB8E0",
		musicGenerationPrice: 8,
		uiSpec: {
			param1: { title: "V1.5", subtitle: "模型版本" },
			param2: { title: "8", subtitle: "灵感 / 次" },
			param3: { title: "歌词 + 旋律", subtitle: "完整生成" },
		},
		isDefault: false,
		has3DModel: false,
		heroArtworkPath: "/images/genart/playlist-default-blue.png",
	},
] as const;

export const GENART_PROMPT_SUGGESTIONS: readonly DesignerPromptSuggestion[] = [
	{
		id: "dream-pop",
		caption: "梦境流行",
		prompt:
			"A dreamy synth-pop melody with airy vocals, warm pads, and a gentle driving bassline.",
		artworkGradient:
			"linear-gradient(135deg, #7373F2 0%, #b18cff 60%, #f0c6ff 100%)",
	},
	{
		id: "night-synth",
		caption: "夜色合成器",
		prompt:
			"Late-night synthesizer vibes with retro arpeggios, smooth electric piano, and a laid-back groove.",
		artworkGradient:
			"linear-gradient(135deg, #1a1340 0%, #3b2a8f 50%, #7373F2 100%)",
	},
	{
		id: "warm-folk",
		caption: "暖意民谣",
		prompt:
			"Warm acoustic folk with gentle guitar fingerpicking, soft strings, and an intimate vocal delivery.",
		artworkGradient:
			"linear-gradient(135deg, #E0589E 0%, #ff9a8b 60%, #ffd194 100%)",
	},
	{
		id: "electronic-pulse",
		caption: "电子脉冲",
		prompt:
			"Pulsing electronic beats with neon synth leads, tight drum machines, and an uplifting buildup.",
		artworkGradient:
			"linear-gradient(135deg, #0a1f3a 0%, #1f6f9e 50%, #3FB8E0 100%)",
	},
] as const;

export function defaultGenArtModel(): GenArtModelDescriptor {
	const found = GENART_MODELS.find((model) => model.isDefault);
	if (found) return found;
	const fallback = GENART_MODELS[0];
	if (fallback) return fallback;
	throw new Error("GENART_MODELS must contain at least one model");
}

export function formatGenerationPrice(price: number): string {
	return `${price} 灵感`;
}

export function generateMusicButtonTitle(
	baseTitle: string,
	price: number | null,
): string {
	if (price === null || price <= 0) return baseTitle;
	return `${baseTitle} · ${price} 灵感`;
}
